import { PlusIcon } from '@langchain/untitled-ui-icons';

import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Button,
  ButtonGroup,
  IconButton,
  Spinner,
  Tooltip,
} from '@/design-system';
import { useListFilterViews } from '@/hooks/useSwr';
import { FilterViewSchema, FilterViewType } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { useFilterBarOpen } from '../RunsTable/hooks/useFilterBarOpen';
import { FilterSettings } from './components/FilterSettings';
import {
  ACTIVE_CLASS_NAMES,
  DEFAULT_FILTERS,
  DEFAULT_FILTER_LS_KEY,
  VIEW_TO_LABEL,
} from './constants';
import { useVisibleFilters } from './hooks/useVisibleFilters';

type Props = {
  filterBarVariant: 'runs' | 'form' | 'run-details';
  onViewChange?: (
    view: 'llm' | 'trace' | 'errors' | string,
    maybeFilter?: string,
    maybeTraceFilter?: string,
    maybeTreeFilter?: string,
    isInitialLoad?: boolean
  ) => void;
  view?: 'llm' | 'trace' | 'errors' | string;
  sessionId?: string;
  experimentalSearchEnabled?: boolean;
};

interface FilterButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const FilterButton: React.FC<FilterButtonProps> = ({
  label,
  active,
  onClick,
  disabled = false,
  disabledTooltip,
}) => {
  const button = (
    <Button
      variant="outlined"
      color="secondary"
      size="sm"
      disabled={disabled}
      className={cn(active && ACTIVE_CLASS_NAMES)}
      onClick={onClick}
    >
      {label}
    </Button>
  );

  if (disabled && disabledTooltip) {
    return <Tooltip title={disabledTooltip}>{button}</Tooltip>;
  }

  return button;
};

type FilterViewsSelectorProps = {
  filterViews: FilterViewSchema[];
  view?: string;
  onViewChange: (
    view: string,
    maybeFilter?: string,
    maybeTraceFilter?: string,
    maybeTreeFilter?: string
  ) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  setFilterBarOpen: (open: boolean) => void;

  defaultFilters: string[];
  setDefaultFilters?: (filters: string[]) => void;
  experimentalSearchEnabled?: boolean;
};
export const FilterViewsSelector: React.FC<FilterViewsSelectorProps> = ({
  filterViews,
  view,
  onViewChange,
  containerRef,
  setFilterBarOpen,
  defaultFilters,
  setDefaultFilters,
  experimentalSearchEnabled,
}) => {
  const [open, setOpen] = useState(false);

  const { visibleFilters, overflowFilters } = useVisibleFilters(
    filterViews,
    containerRef,
    view
  );

  const showDefaultFilters = setDefaultFilters !== undefined;
  const disabledTooltip =
    'Pre-saved filters are disabled in experimental search mode';

  return (
    <>
      {visibleFilters.map((fv) => (
        <FilterButton
          key={fv.id}
          label={fv.display_name}
          active={view === fv.id}
          onClick={() =>
            onViewChange(
              fv.id,
              fv.filter_string,
              fv.trace_filter_string,
              fv.tree_filter_string
            )
          }
          disabled={experimentalSearchEnabled}
          disabledTooltip={disabledTooltip}
        />
      ))}
      {overflowFilters.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outlined" color="secondary" size="sm">
              ...
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom" className="w-auto p-0">
            <div className="flex flex-col divide-y divide-secondary">
              {overflowFilters.map((fv) => (
                <FilterButton
                  key={fv.id}
                  label={fv.display_name}
                  active={view === fv.id}
                  onClick={() => {
                    onViewChange(
                      fv.id,
                      fv.filter_string,
                      fv.trace_filter_string,
                      fv.tree_filter_string
                    );
                    setOpen(false);
                  }}
                  disabled={!!experimentalSearchEnabled}
                  disabledTooltip={disabledTooltip}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      <IconButton
        color="secondary"
        onClick={() => {
          setFilterBarOpen(true);
          onViewChange(showDefaultFilters ? 'trace' : '');
        }}
        icon={PlusIcon}
        label="Add Filter"
      />
      <FilterSettings
        savedFilters={filterViews}
        onToggleSwitch={() => {
          onViewChange(showDefaultFilters ? 'trace' : '');
        }}
        defaultFilters={defaultFilters}
        setDefaultFilters={setDefaultFilters}
      />
    </>
  );
};

const FilterViewsSelect = ({
  onViewChange,
  view,
  sessionId,
  filterBarVariant,
  experimentalSearchEnabled,
}: Props) => {
  const { setOpen: setFilterBarOpen } = useFilterBarOpen(filterBarVariant);
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useListFilterViews(
    sessionId,
    FilterViewType.RUNS,
    {
      onSuccess: (data) => {
        const customViewId = searchParams.get('custom_filter_view_id');
        const customView = data.find((view) => view.id === customViewId);

        if (customView) {
          onViewChange?.(
            customView.id,
            customView.filter_string,
            customView.trace_filter_string,
            customView.tree_filter_string,
            true
          );
        }
      },
    }
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const [defaultFilters, setDefaultFilters] = useLocalStorageState(
    DEFAULT_FILTER_LS_KEY,
    DEFAULT_FILTERS
  );

  return (
    onViewChange && (
      <div ref={containerRef} className="flex flex-1">
        <ButtonGroup>
          {defaultFilters
            .filter((filter) => filter !== 'all') // Ignore since we removed the default 'all' filter
            .map((filter) => (
              <FilterButton
                key={filter}
                label={VIEW_TO_LABEL[filter]}
                active={view === filter}
                onClick={() => onViewChange(filter)}
                disabled={!!experimentalSearchEnabled}
                disabledTooltip="Pre-saved filters are disabled in experimental search mode"
              />
            ))}

          {data && data.length > 0 && (
            <FilterViewsSelector
              containerRef={containerRef}
              filterViews={data}
              view={view}
              onViewChange={onViewChange}
              setFilterBarOpen={setFilterBarOpen}
              defaultFilters={defaultFilters}
              setDefaultFilters={setDefaultFilters}
              experimentalSearchEnabled={experimentalSearchEnabled}
            />
          )}
          {isLoading && (
            <div className="flex items-center justify-center px-1">
              <Spinner />
            </div>
          )}
        </ButtonGroup>
      </div>
    )
  );
};

export { FilterViewsSelect };
